import { IDigestEmail } from 'types'
import { ICarrierTaskAutomationConfig } from 'types/carrier'
import { ENDPOINTS } from 'utils/api/callApi'
import { keymirror } from 'utils/keymirror'
import { generateApiActionTypes } from './generateActionTypes'

export const CARRIER_ACTION_TYPES = keymirror({
  CARRIER_REQUEST: undefined,
  CARRIER_SUCCESS: undefined,
  CARRIER_FAILURE: undefined,
})

export const CARRIER_UPDATE_ACTION_TYPES = keymirror({
  CARRIER_UPDATE_REQUEST: undefined,
  CARRIER_UPDATE_SUCCESS: undefined,
  CARRIER_UPDATE_FAILURE: undefined,
})

export const REQUEST_CARRIER_APPROVAL_ACTION_TYPES = generateApiActionTypes(
  'REQUEST_CARRIER_APPROVAL'
)

export const CARRIER_REQUEST_APPROVAL_ACTION_TYPES = keymirror({
  CARRIER_REQUEST_APPROVAL_REQUEST: undefined,
  CARRIER_REQUEST_APPROVAL_SUCCESS: undefined,
  CARRIER_REQUEST_APPROVAL_FAILURE: undefined,
})

export const DOCUMENT_REVIEWING_ACTION_TYPES = keymirror({
  DOCUMENT_REVIEWING_REQUEST: undefined,
  DOCUMENT_REVIEWING_SUCCESS: undefined,
  DOCUMENT_REVIEWING_FAILURE: undefined,
})

export const FMCSA_ACTION_TYPES = keymirror({
  FMCSA_REQUEST: undefined,
  FMCSA_SUCCESS: undefined,
  FMCSA_FAILURE: undefined,
})

export const CARRIER_COMMODITIES_ACTION_TYPE = generateApiActionTypes('CARRIER_COMMODITIES')
export const CARRIER_REFERENCES_ACTION_TYPE = generateApiActionTypes('CARRIER_REFERENCES')
export const EDIT_CARRIER_COMMODITIES_ACTION_TYPE = generateApiActionTypes(
  'EDIT_CARRIER_COMMODITIES'
)
export const EDIT_CARRIER_REFERENCES_ACTION_TYPE = generateApiActionTypes('EDIT_CARRIER_REFERENCES')

export const INSURANCE_ACTION_TYPES = keymirror({
  CARRIER_INSURANCE_REQUEST: undefined,
  CARRIER_INSURANCE_SUCCESS: undefined,
  CARRIER_INSURANCE_FAILURE: undefined,
})

export const INSURANCE_UPDATE_ACTION_TYPES = keymirror({
  CARRIER_INSURANCE_UPDATE_REQUEST: undefined,
  CARRIER_INSURANCE_UPDATE_SUCCESS: undefined,
  CARRIER_INSURANCE_UPDATE_FAILURE: undefined,
})

export const CARRIER_CHECKLIST_ACTION_TYPES = generateApiActionTypes('CHECKLIST')
export const CARRIER_UPLOADED_DOCS_ACTION_TYPES = generateApiActionTypes('UPLOADED_DOCS')
export const CARRIER_CHECKLIST_UPDATE_ACTION_TYPES = generateApiActionTypes('EDIT_CHECKLIST')
export const CARRIER_UPLOAD_DOCS_ACTION_TYPES = generateApiActionTypes('UPLOAD_DOCUMENTS')

export const CARRIER_EDIT_UPLOAD_ACTION_TYPES = keymirror({
  ADD_DOCUMENT: undefined,
  ADD_DOCUMENT_FAILURE: undefined,
  DELETE_DOCUMENT: undefined,
})

export const getCarrierDetails = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_ACTION_TYPES),
    },
  }
}

export const requestCarrierApproval = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'request_approval',
    method: 'PUT',
    skipErrorPrefix: true,
    body: {
      carrierApplication: {
        requestedById: true,
      },
    },
  }

  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(REQUEST_CARRIER_APPROVAL_ACTION_TYPES),
    },
  }
}

export const CARRIER_CREATE_VENDOR_ACTION_TYPES = generateApiActionTypes('CARRIER_CREATE_VENDOR')

export const CARRIER_UPDATE_VENDOR_ACTION_TYPES = generateApiActionTypes('CARRIER_UPDATE_VENDOR')
const pushToNetsuite = (id: string, actionTypes?: string[]) => ({
  payload: {
    callApi: true,
    requestData: {
      method: 'PUT',
      endpoint: ENDPOINTS.carriers,
      id,
      group: 'export',
    },
    actionTypes,
  },
})

export const createVendor = (id: string) =>
  pushToNetsuite(id, Object.keys(CARRIER_CREATE_VENDOR_ACTION_TYPES))
export const updateVendor = (id: string) =>
  pushToNetsuite(id, Object.keys(CARRIER_UPDATE_VENDOR_ACTION_TYPES))

interface ICarrierDetailsPayload {
  [key: string]: any
}
export const updateCarrierDetails = (
  id: number,
  payload: ICarrierDetailsPayload,
  origin: string
) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    method: 'PUT',
    body: {
      carrierApplication: payload,
    },
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_UPDATE_ACTION_TYPES),
      origin,
    },
  }
}

export const carrierRequestApproval = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'request_send_packet',
    method: 'PUT',
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_REQUEST_APPROVAL_ACTION_TYPES),
    },
  }
}

export const sendDocumentReviewingEmail = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    group: 'send_document_reviewing_email',
    method: 'PUT',
    id,
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(DOCUMENT_REVIEWING_ACTION_TYPES),
    },
  }
}

export const getFMCSA = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'fmcsa_carrier_information',
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(FMCSA_ACTION_TYPES),
    },
  }
}

export const getCarrierInsurance = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'insurances',
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(INSURANCE_ACTION_TYPES),
    },
  }
}

// TODO: Type Insurance payload
export const updateCarrierInsurance = (id: string, payload: any) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'insurances',
    method: 'PUT',
    body: {
      insurances: payload,
    },
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(INSURANCE_UPDATE_ACTION_TYPES),
    },
  }
}

export const getCarrierChecklist = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'ops_carrier_checklist',
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_CHECKLIST_ACTION_TYPES),
    },
  }
}

export const getCarrierUploadedDocs = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'package_files',
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_UPLOADED_DOCS_ACTION_TYPES),
    },
  }
}

// TODO: Type opsCarrierChecklist payload
export const editCarrierChecklist = (id: string, opsCarrierChecklist: any) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'ops_carrier_checklist',
    method: 'POST',
    body: {
      opsCarrierChecklist,
    },
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_CHECKLIST_UPDATE_ACTION_TYPES),
    },
  }
}

// TODO: TYPE packageFiles payload
export const uploadCarrierDocuments = (id: string, packageFiles: any) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'package_files',
    method: 'POST',
    body: {
      packageFiles,
    },
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_UPLOAD_DOCS_ACTION_TYPES),
    },
  }
}

// TODO: Type files
export const addDocUpload = (files: any) => ({
  type: CARRIER_EDIT_UPLOAD_ACTION_TYPES.ADD_DOCUMENT,
  files,
})

export const deleteDocUpload = (id: string) => ({
  type: CARRIER_EDIT_UPLOAD_ACTION_TYPES.DELETE_DOCUMENT,
  id,
})

export const getCarrierCommodities = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'covered_commodities',
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_COMMODITIES_ACTION_TYPE),
    },
  }
}

// TODO: Type
export const editCarrierCommodities = (id: string, coveredCommodities: any) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'covered_commodities',
    method: 'PUT',
    body: {
      coveredCommodities,
    },
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(EDIT_CARRIER_COMMODITIES_ACTION_TYPE),
    },
  }
}

export const COMMODITIES_DIALOG_ACTION_TYPES = keymirror({
  OPEN_COMMODITIES_DIALOG: undefined,
  CLOSE_COMMODITIES_DIALOG: undefined,
})

export const showCommoditiesDialog = () => ({
  type: COMMODITIES_DIALOG_ACTION_TYPES.OPEN_COMMODITIES_DIALOG,
})

export const closeCommoditiesDialog = () => ({
  type: COMMODITIES_DIALOG_ACTION_TYPES.CLOSE_COMMODITIES_DIALOG,
})

export const REFERENCES_DIALOG_ACTION_TYPES = keymirror({
  OPEN_REFERENCES_DIALOG: undefined,
  CLOSE_REFERENCES_DIALOG: undefined,
})

export const showReferencesDialog = () => ({
  type: REFERENCES_DIALOG_ACTION_TYPES.OPEN_REFERENCES_DIALOG,
})

export const closeReferencesDialog = () => ({
  type: REFERENCES_DIALOG_ACTION_TYPES.CLOSE_REFERENCES_DIALOG,
})

export const getCarrierReferences = (id: string) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'carrier_references',
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_REFERENCES_ACTION_TYPE),
    },
  }
}

// TODO: Type
export const editCarrierReferences = (id: string, carrierReferences: any) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    group: 'carrier_references',
    method: 'PUT',
    body: {
      carrierReferences,
    },
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(EDIT_CARRIER_REFERENCES_ACTION_TYPE),
    },
  }
}
export const CARRIER_STATUS_MODAL = keymirror({
  CARRIER_STATUS_MODAL_OPEN: undefined,
  CARRIER_STATUS_MODAL_CLOSE: undefined,
})

export const openCarrierStatusModal = () => ({
  type: CARRIER_STATUS_MODAL.CARRIER_STATUS_MODAL_OPEN,
})

export const closeCarrierStatusModal = () => ({
  type: CARRIER_STATUS_MODAL.CARRIER_STATUS_MODAL_CLOSE,
})

export const CARRIER_EDIT_STATUS_ACTION_TYPES = generateApiActionTypes('CARRIER_EDIT_STATUS')

// TODO: Type
export const updateCarrierStatus = (id: string, payload: any) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    method: 'PUT',
    group: 'update_state',
    body: payload,
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(CARRIER_EDIT_STATUS_ACTION_TYPES),
    },
  }
}

export const CONTACT_INSTRUCTIONS_MODAL = keymirror({
  CONTACT_INSTRUCTIONS_MODAL_OPEN: undefined,
  CONTACT_INSTRUCTIONS_MODAL_CLOSE: undefined,
})

export const openContactInstructionsModal = () => ({
  type: CONTACT_INSTRUCTIONS_MODAL.CONTACT_INSTRUCTIONS_MODAL_OPEN,
})

export const closeContactInstructionsModal = () => ({
  type: CONTACT_INSTRUCTIONS_MODAL.CONTACT_INSTRUCTIONS_MODAL_CLOSE,
})
export const EDIT_CONTACT_INSTRUCTIONS_ACTION_TYPE = generateApiActionTypes(
  'EDIT_CONTACT_INSTRUCTIONS'
)

interface ICarrierContactInstructionsPayload {
  carrierSpecificInfo: string
  bulkEmailEnabled: boolean
  carrierBulkEmailRecipientsAttributes: Partial<IDigestEmail>[]
}

export const editContactInstructions = (
  id: string,
  payload: ICarrierContactInstructionsPayload
) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    method: 'PUT',
    group: 'specific_info',
    body: payload,
  }
  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(EDIT_CONTACT_INSTRUCTIONS_ACTION_TYPE),
    },
  }
}

export const EDIT_CARRIER_TASK_AUTOMATION_CONFIG_ACTION_TYPES = generateApiActionTypes(
  'EDIT_CARRIER_TASK_AUTOMATION_CONFIG'
)

export const editCarrierTaskAutomationConfig = (
  id: string,
  payload: ICarrierTaskAutomationConfig
  // We use the same type for the state and the payload
) => {
  const requestData = {
    endpoint: ENDPOINTS.carriers,
    id,
    method: 'PUT',
    group: 'task_automation_config',
    body: payload,
  }

  return {
    payload: {
      callApi: true,
      requestData,
      actionTypes: Object.keys(EDIT_CARRIER_TASK_AUTOMATION_CONFIG_ACTION_TYPES),
    },
  }
}
